import React, { useState, useEffect } from 'react';
import Navbar from "../components/studentnavbar";
import Sidebar from '../components/studentsidebar';
import { useSidebar } from '../components/SidebarContext';
import api from "../services/api";

const StudentPayments = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStudentPayments = async () => {
      try {
        console.log('Starting to fetch student payments...');
        
        // Get student ID from localStorage - check all possible keys
        let studentId = localStorage.getItem('id'); // This is where it's stored in login
        
        // If not found, try getting from student object
        if (!studentId) {
          const studentData = localStorage.getItem('student');
          if (studentData) {
            try {
              const student = JSON.parse(studentData);
              studentId = student._id || student.id;
            } catch (parseError) {
              console.error('Error parsing student data:', parseError);
            }
          }
        }
        
        if (!studentId) {
          setError("Student ID not found. Please log in again.");
          setLoading(false);
          return;
        }
        
        console.log('Student ID found:', studentId);
        
        // Send student ID as query parameter
        const response = await api.get(`/api/payments/student-payments?studentId=${studentId}`);
        console.log('Response received:', response.data);
        
        // Set the payments data
        setPayments(response.data);
        setFilteredPayments(response.data);
      } catch (err) {
        console.error('Detailed error:', err);
        console.error('Error message:', err.message);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        
        if (err.code === 'ECONNABORTED') {
          setError("Request timed out. Please try again.");
        } else if (err.response?.status === 404) {
          setError("Payment endpoint not found. Please contact support.");
        } else if (err.response?.status === 401) {
          setError("Authentication required. Please log in again.");
        } else if (err.response?.status === 400) {
          setError("Invalid request. Please log in again.");
        } else {
          setError("Failed to fetch payment data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudentPayments();
  }, []);

  useEffect(() => {
    const filtered = payments.filter(payment => {
      const testName = (payment.testName || '').toLowerCase();
      const amount = (payment.amount || '').toString().toLowerCase();
      const method = (payment.method || '').toLowerCase();
      const status = (payment.status || '').toLowerCase();
      const transactionId = (payment.transactionId || '').toLowerCase();
      const date = new Date(payment.date).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).toLowerCase();
      const search = searchQuery.toLowerCase();

      return (
        testName.includes(search) ||
        amount.includes(search) ||
        method.includes(search) ||
        status.includes(search) ||
        transactionId.includes(search) ||
        date.includes(search)
      );
    });
    setFilteredPayments(filtered);
  }, [searchQuery, payments]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
      case 'used':
        return { display: 'Success', color: '#22c55e' }; // Green
      case 'failed':
      case 'pending':
      case 'created':
        return { display: 'Failed', color: '#ef4444' }; // Red
      default:
        return { display: status, color: '#6b7280' }; // Gray
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    // Convert from smallest unit to main currency unit
    const convertedAmount = amount / 100;
    
    if (currency === 'INR') {
      // Format with 2 decimal places for INR
      return `₹${convertedAmount.toFixed(2)}`;
    }
    
    // For other currencies, also convert and format with 2 decimals
    return `${currency} ${convertedAmount.toFixed(2)}`;
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: "100vh",
      background: "linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)",
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 10,
      transition: 'width 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      height: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#6B21A8',
      margin: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'auto',
      border: 'none',
      borderRadius: '8px',
      maxHeight: 'calc(100vh - 12rem)',
      height: 'auto',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: 'none',
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    headerRow: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'center',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    row: {
      borderRadius: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      padding: '10px 12px',
      verticalAlign: 'middle',
      textAlign: 'center',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '500',
      color: 'white',
      textTransform: 'capitalize',
      fontFamily: "'Roboto', sans-serif",
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#666',
      fontFamily: "'Roboto', sans-serif",
    },
    errorContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: 'red',
      fontFamily: "'Roboto', sans-serif",
    },
    noDataContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        input:focus {
          border-color: #6B21A8 !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table th,
        .custom-table td {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(even) {
          background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:hover {
          background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .table-container::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 6px;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-thumb {
          background: #6c757d;
          border-radius: 6px;
          border: 2px solid #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #495057;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar-corner {
          background: #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container {
          scrollbar-width: thin;
          scrollbar-color: #6c757d #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
        }
        .search-input {
          font-family: 'Roboto', sans-serif !important;
        }
      `}</style>
      <div style={styles.sidebar}>
        <Sidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'all 0.3s ease', fontFamily: "'Roboto', sans-serif" }}>
        <Navbar />
        <div style={styles.content}>
          <div style={styles.headerContainer}>
            <h2 style={styles.title}></h2>
            <div style={styles.searchFilterGroup}>
              <input
                type="text"
                placeholder="Search payments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
                className="search-input"
                aria-label="Search payments"
              />
            </div>
          </div>
          
          {loading ? (
            <div style={styles.loadingContainer}>
              <p>Loading payment history...</p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <p>{error}</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div style={styles.noDataContainer}>
              <p>No payment records available.</p>
            </div>
          ) : (
            <div style={styles.tableContainer} className="table-container">
              <table className="custom-table" style={styles.table}>
                <thead style={styles.headerRow}>
                  <tr>
                    <th style={styles.th}>Sr.</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Test Name</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Payment Method</th>
                    <th style={styles.th}>Transaction ID</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, index) => (
                    <tr
                      key={payment.id}
                      style={styles.row}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)';
                      }}
                    >
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>
                        {new Date(payment.date).toLocaleDateString("en-GB", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td style={styles.td}>{payment.testName}</td>
                      <td style={styles.td}>{formatCurrency(payment.amount, payment.currency)}</td>
                      <td style={styles.td}>{payment.method}</td>
                      <td style={styles.td}>{payment.transactionId}</td>
                      <td style={styles.td}>
                        <span 
                          style={{
                            ...styles.statusBadge,
                            backgroundColor: getStatusColor(payment.status).color
                          }}
                        >
                          {getStatusColor(payment.status).display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentPayments;